import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { getConfig, isProduction } from '../../config/appConfig';
import apiService from '../../services/apiService';
import { getAuthApiKey, getAuthUser, candorWebRequestAuth, hasCookieSession } from '../../utils/authTokenStore';
import paddleService from '../../services/paddleService';
import { apiUrl } from '../../utils/apiBase';

// Start Code - dc - 11062025
// Helper function to determine badge and highlight from plan data
const getPlanBadge = (tokens, planName) => {
    // Determine badge based on token count or plan name
    if (tokens <= 100000) return 'Starter';
    if (tokens <= 500000) return 'Popular';
    if (tokens <= 1000000) return 'Best value';
    return 'Enterprise';
};

const isHighlighted = (tokens) => {
    // Highlight the 500k pack (Popular) by default
    return tokens === 500000;
};

// Format plan name for display
const formatPlanName = (planName, tokens) => {
    // If plan_name is like "add-on-1M", extract and format
    if (planName && planName.toLowerCase().includes('add-on')) {
        // Try to extract number from plan name
        const match = planName.match(/(\d+)([kmKM])?/);
        if (match) {
            const num = parseInt(match[1]);
            const unit = match[2] ? match[2].toUpperCase() : '';
            return `${num}${unit} Token Pack`;
        }
    }
    // Fallback: format from tokens
    if (tokens >= 1000000) {
        return `${(tokens / 1000000).toFixed(1)}M Token Pack`.replace('.0', '');
    } else if (tokens >= 1000) {
        return `${(tokens / 1000).toFixed(0)}k Token Pack`;
    }
    return `${tokens.toLocaleString()} Token Pack`;
};

const AddOnPlans = () => {
    const [addOns, setAddOns] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [processingPlan, setProcessingPlan] = useState(null);

    // Mock Checkout Modal State
    const [showMockCheckout, setShowMockCheckout] = useState(false);
    const [mockCheckoutPlan, setMockCheckoutPlan] = useState(null); // { planId, planName, price, sessionId }
    const [mockCheckoutProcessing, setMockCheckoutProcessing] = useState(false);
    const [mockCheckoutError, setMockCheckoutError] = useState(null);
    const [mockCardForm, setMockCardForm] = useState({ number: '', expiry: '', cvv: '', name: '' });
    const [mockCheckoutSuccess, setMockCheckoutSuccess] = useState(false);
    const [checkoutMode, setCheckoutMode] = useState(() => {
        // ***** BH29072026_1700 — production always uses the Paddle overlay; a stale
        // candor_checkout_mode value must not route a live user into the dev card form.
        if (isProduction()) return 'paddle_sdk';
        const saved = localStorage.getItem('candor_checkout_mode');
        return saved !== null ? saved : 'paddle_sdk';
    });

    const formatCardNumber = (value) => {
        return value.replace(/\D/g, '').replace(/(\d{4})/g, '$1 ').trim().slice(0, 19);
    };

    const formatExpiry = (value) => {
        const clean = value.replace(/\D/g, '').slice(0, 4);
        if (clean.length >= 3) return `${clean.slice(0, 2)}/${clean.slice(2)}`;
        return clean;
    };

    const formatPriceValue = (value) => {
        if (value === null || value === undefined) {
            return '0';
        }
        const rounded = Math.round(Number(value) * 100) / 100;
        return Number.isInteger(rounded) ? rounded.toLocaleString() : rounded.toFixed(2);
    };

    useEffect(() => {
        const loadAddonPlans = async () => {
            try {
                setLoading(true);
                setError(null);

                const apiKey = getAuthApiKey();
                if (!apiKey && !hasCookieSession()) {
                    setError('Authentication required');
                    setLoading(false);
                    return;
                }

                const response = await apiService.getAddonPlans();

                if (response && response.status === 'success' && Array.isArray(response.plans)) {
                    // Transform database plans to UI format
                    const transformedPlans = response.plans.map((plan) => {
                        const tokens = plan.tokens || plan.monthly_limit || 0;
                        const planName = plan.plan_name || '';

                        const transformed = {
                            id: `addon_${plan.id}`,
                            name: formatPlanName(planName, tokens),
                            tokens: tokens,
                            price: plan.price || 0,
                            badge: getPlanBadge(tokens, planName),
                            highlight: isHighlighted(tokens),
                            originalPlan: plan // Keep original for reference
                        };
                        return transformed;
                    });

                    // Sort by tokens ascending
                    transformedPlans.sort((a, b) => a.tokens - b.tokens);

                    setAddOns(transformedPlans);
                } else {
                    setError('Failed to load add-on plans - invalid response format');
                }
            } catch (err) {
                setError(err.message || 'Failed to load add-on plans');
            } finally {
                setLoading(false);
            }
        };

        loadAddonPlans();
    }, []);

    const handlePurchaseAddon = async (plan) => {
        try {
            setProcessingPlan(plan.id);
            
            // Get plan_id from the original plan data
            // Use the original database ID directly (integer or string)
            // Backend will handle matching both integer IDs and prefixed string IDs
            const planId = plan.originalPlan?.id ?? plan.id;
            
            // If plan.id is a prefixed string like 'addon_4', extract the numeric part
            // Otherwise use the original database ID
            let finalPlanId = planId;
            if (typeof planId === 'string' && planId.startsWith('addon_')) {
                // Extract numeric ID from 'addon_4' -> 4
                const numericId = planId.replace('addon_', '');
                finalPlanId = numericId; // Send as string '4' or convert to number
            } else if (plan.originalPlan?.id) {
                // Use the original database ID directly
                finalPlanId = plan.originalPlan.id;
            }
            
            // Build success and cancel URLs
            const buildSuccessUrl = getConfig('paddle.buildSuccessUrl');
            const buildCancelUrl = getConfig('paddle.buildCancelUrl');
            const successUrl = typeof buildSuccessUrl === 'function' 
                ? buildSuccessUrl(finalPlanId) 
                : `${window.location.origin}/success?success=true&plan=${finalPlanId}&transaction_id={TRANSACTION_ID}`;
            const cancelUrl = typeof buildCancelUrl === 'function' 
                ? buildCancelUrl(finalPlanId) 
                : `${window.location.origin}/success?success=false&plan=${finalPlanId}`;
            
            // Get user email for checkout session
            const candorUserObj = getAuthUser();
            const candorUser = candorUserObj ? JSON.stringify(candorUserObj) : null;
            let customerEmail = null;
            if (candorUser) {
                try {
                    const userData = JSON.parse(candorUser);
                    customerEmail = userData.email;
                } catch (error) {
                    // Ignore parsing errors to avoid exposing user data
                }
            }
            
            // Prepare plan data for validation (backend will verify against database)
            const planData = {
                name: plan.name || plan.originalPlan?.plan_name,
                price: plan.price || plan.originalPlan?.price,
                monthly_limit: plan.tokens || plan.originalPlan?.monthly_limit
            };
            
            // Create checkout session - backend will fetch plan details from database using plan_id
            const session = await paddleService.createCheckoutSession(
                finalPlanId,
                successUrl,
                cancelUrl,
                customerEmail,
                planData
            );

            // Get transaction_id from response
            const transactionId = session.transaction_id || session.session_id;
            
            if (!transactionId) {
                throw new Error('No transaction ID received from checkout session');
            }
            
            // Get checkout URL if available (may be null for draft transactions)
            const checkoutUrl = session.url || session.checkout_url;
            
            // CRITICAL: Paddle does NOT support direct checkout URL redirects - they all return 404
            // The ONLY way to open Paddle checkout is using Paddle.js Checkout.open() with transaction ID
            // We MUST ignore ALL checkout URLs and use Paddle.js instead
            
            if (checkoutUrl) {
                // Always ignore direct checkout URLs to avoid leaking user data via console logs
            }
            
            let sdkFailureReason = null;
            if (checkoutMode === 'paddle_sdk') {
                try {
                    const isRealPaddleTxn = transactionId && typeof transactionId === 'string' && transactionId.startsWith('txn_') && !transactionId.includes('mock') && !transactionId.includes('dummy');
                    
                    // Check if we can fallback to client-side price ID mapping for the Paddle Overlay
                    // ***** BH29072026_1530 — pass the DB row so uuid ids resolve to a pri_
                    const priceId = paddleService.mapPlanIdToPriceId(finalPlanId, 'monthly', plan.originalPlan);
                    const hasPriceId = priceId && typeof priceId === 'string' && priceId.startsWith('pri_');
                    
                    if (!isRealPaddleTxn && !hasPriceId) {
                        // ***** BH29072026_1700 — name the missing price var for this pack
                        throw new Error(`No Paddle price is configured for add-on "${plan.originalPlan?.plan_name || finalPlanId}", and the backend returned a non-Paddle transaction id "${transactionId}". Set REACT_APP_PADDLE_PRICE_ADDON_* in .env.production and PADDLE_API_KEY / PADDLE_ENVIRONMENT for llm_server.`);
                    }
                    await paddleService.redirectToCheckout(
                        transactionId,
                        null,
                        successUrl,
                        cancelUrl,
                        finalPlanId,
                        'monthly',
                        plan.originalPlan
                    );
                    return;
                } catch (sdkError) {
                    sdkFailureReason = sdkError.message || 'Failed to initialize the Paddle SDK.';
                    console.warn(sdkFailureReason);
                }
            }

            // ***** Code Start BH29072026_1700 - Never collect card details outside Paddle in production
            // The modal below is a development stand-in; on the live site it looks like a real
            // checkout, is not PCI compliant, and masks the Paddle misconfiguration underneath.
            if (isProduction()) {
                throw new Error(
                    `Could not open the Paddle checkout, so no payment was started. ${sdkFailureReason || ''} `.trim() +
                        ' Please do not enter card details anywhere else — this needs a Paddle configuration fix.'
                );
            }
            // ***** Code End BH29072026_1700
            
            // Fallback: Open our unified custom modal with card inputs
            setMockCheckoutPlan({
                planId: finalPlanId,
                planName: plan.name || plan.originalPlan?.plan_name || "Token Pack",
                price: plan.price || plan.originalPlan?.price,
                sessionId: transactionId,
                successUrl
            });
            setMockCardForm({ number: '', expiry: '', cvv: '', name: '' });
            setMockCheckoutError(null);
            setMockCheckoutSuccess(false);
            setShowMockCheckout(true);
            
        } catch (error) {
            alert(`Failed to start checkout: ${error.message || 'Unknown error'}`);
        } finally {
            setProcessingPlan(null);
        }
    };

    const handleMockCheckoutSubmit = async (e) => {
        e.preventDefault();
        if (!mockCheckoutPlan) return;

        const rawNumber = mockCardForm.number.replace(/\s/g, '');
        if (rawNumber.length < 13 || rawNumber.length > 19) {
            setMockCheckoutError('Please enter a valid card number (13–19 digits).');
            return;
        }
        if (!mockCardForm.expiry.match(/^(0[1-9]|1[0-2])\/\d{2}$/)) {
            setMockCheckoutError('Expiry must be in MM/YY format.');
            return;
        }
        if (mockCardForm.cvv.length < 3) {
            setMockCheckoutError('Please enter a valid CVV (3–4 digits).');
            return;
        }
        if (!mockCardForm.name.trim()) {
            setMockCheckoutError('Please enter the cardholder name.');
            return;
        }

        setMockCheckoutProcessing(true);
        setMockCheckoutError(null);

        try {
            const apiKey = getAuthApiKey();

            const headers = candorWebRequestAuth({ 'Content-Type': 'application/json' }).headers;

            const verifyData = await apiService.verifyCheckoutSession({
                sessionId: mockCheckoutPlan.sessionId,
                planId: mockCheckoutPlan.planId,
            });

            if (verifyData?.verified || verifyData?.success || verifyData?.status === 'success') {
                setMockCheckoutSuccess(true);
                // Trigger background update if event listener exists
                if (window.dispatchEvent) {
                    window.dispatchEvent(new CustomEvent('subscriptionUpdate'));
                }
            } else {
                setMockCheckoutError(verifyData.detail || verifyData.message || 'Verification failed. Please try again.');
            }
        } catch (err) {
            setMockCheckoutError('Network error. Please check your connection and try again.');
        } finally {
            setMockCheckoutProcessing(false);
        }
    };

    if (loading) {
        return (
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <div className="text-center">
                    <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
                    <p className="mt-4 text-slate-600">Loading add-on plans...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <div className="text-center">
                    <p className="text-red-600">Error: {error}</p>
                    <button
                        onClick={() => window.location.reload()}
                        className="mt-4 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                    >
                        Retry
                    </button>
                </div>
            </div>
        );
    }

    if (addOns.length === 0) {
        return (
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <div className="text-center">
                    <p className="text-slate-600">No add-on plans available at this time.</p>
                </div>
            </div>
        );
    }

    const badgeColors = {
        'Starter': 'from-sky-500 to-cyan-500',
        'Popular': 'from-violet-500 to-fuchsia-500',
        'Best value': 'from-amber-500 to-orange-500',
        'Enterprise': 'from-indigo-600 to-purple-600'
    };

    const baseFeatures = [
        'Adds to current token balance instantly',
        'Never expires — use anytime',
        'Works with any active plan'
    ];

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-100 via-white to-indigo-50/40 py-16 px-4 sm:px-6 lg:px-8 overflow-x-hidden">
            <div className="max-w-7xl mx-auto">
                {/* Header */}
                <div className="text-center mb-16">
                    <div className="mx-auto mb-8 w-24 h-24 rounded-3xl bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-500 flex items-center justify-center shadow-2xl shadow-indigo-500/30">
                        <svg className="w-12 h-12 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
                            <path d="M12 5v14M5 12h14" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                    </div>
                    <h1 className="text-4xl md:text-5xl font-extrabold tracking-tight text-slate-900 mb-4">
                        Add-on Plans
                    </h1>
                    <p className="text-lg md:text-xl text-slate-600 max-w-3xl mx-auto">
                        Top up your workspace tokens instantly with flexible packs designed to scale with your team.
                    </p>
                    <div className="mt-6 inline-flex flex-wrap items-center gap-2 bg-white/70 backdrop-blur-sm border border-indigo-100 text-indigo-700 px-5 py-2 rounded-full shadow-sm">
                        <span className="flex items-center gap-2 text-sm font-medium">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                            </svg>
                            Instant credit
                        </span>
                        <span className="text-indigo-200">•</span>
                        <span className="text-sm font-medium">Never expires</span>
                        <span className="text-indigo-200">•</span>
                        <span className="text-sm font-medium">Applies to any workspace</span>
                    </div>
                </div>

                {/* Plans */}
                <div className="grid gap-8 md:grid-cols-2 xl:grid-cols-3 px-4">
                    {addOns.map((p) => {
                        const badgeColor = badgeColors[p.badge] || 'from-slate-700 to-slate-900';
                        const priceDisplay = formatPriceValue(p.price);
                        const costPerThousand = p.originalPlan?.cost_per_token
                            ? Number(p.originalPlan.cost_per_token).toFixed(3)
                            : null;

                        return (
                            <div
                                key={p.id}
                                className={`group relative rounded-3xl border-2 transition-all duration-500 overflow-visible bg-white/90 backdrop-blur shadow-lg hover:shadow-2xl hover:-translate-y-2 ${p.highlight
                                    ? 'border-indigo-500 ring-4 ring-indigo-100'
                                    : 'border-slate-200 hover:border-indigo-200'
                                    }`}
                            >
                                <div
                                    className={`absolute inset-0 rounded-3xl opacity-0 pointer-events-none transition-opacity duration-500 ${p.highlight
                                        ? 'bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50'
                                        : 'bg-gradient-to-br from-slate-50 via-white to-indigo-50'
                                        } group-hover:opacity-100`}
                                />

                                {p.badge && (
                                    <div className={`absolute -top-3 left-6 z-10 px-4 py-1.5 text-xs font-semibold uppercase tracking-wide text-white shadow-xl rounded-full bg-gradient-to-r ${badgeColor}`}>
                                        {p.badge}
                                    </div>
                                )}

                                <div className="relative p-8 pt-10">
                                    <div className="mb-6">
                                        <h3 className="text-2xl font-bold text-slate-900 mb-2">{p.name}</h3>
                                        <p className="text-sm font-medium text-indigo-600 bg-indigo-50 border border-indigo-100 inline-flex items-center gap-2 px-3 py-1 rounded-full">
                                            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <path d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" strokeLinecap="round" strokeLinejoin="round" />
                                            </svg>
                                            {p.tokens.toLocaleString()} tokens
                                        </p>
                                    </div>

                                    <div className="mb-8 pb-6 border-b border-slate-200">
                                        <div className="flex items-baseline gap-2">
                                            <span className="text-5xl font-extrabold text-slate-900">${priceDisplay}</span>
                                            <span className="text-slate-500 font-medium">one-time</span>
                                        </div>
                                        {costPerThousand && (
                                            <p className="mt-2 text-sm text-slate-500">${costPerThousand} per 1,000 tokens</p>
                                        )}
                                    </div>

                                    <ul className="space-y-4 mb-8">
                                        {[...baseFeatures].map((feature, idx) => (
                                            <li key={idx} className="flex items-start text-slate-700 gap-3">
                                                <span className="flex h-6 w-6 items-center justify-center rounded-full bg-gradient-to-r from-green-400 to-emerald-500 text-white shadow">
                                                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.5">
                                                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                                    </svg>
                                                </span>
                                                <span className="text-sm font-medium leading-relaxed">{feature}</span>
                                            </li>
                                        ))}
                                    </ul>

                                    <button
                                        onClick={() => handlePurchaseAddon(p)}
                                        disabled={processingPlan === p.id}
                                        className={`w-full flex items-center justify-center gap-2 rounded-xl py-3.5 text-sm font-semibold shadow-lg transition-all duration-300 ${processingPlan === p.id
                                            ? 'opacity-50 cursor-not-allowed'
                                            : p.highlight
                                                ? 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white hover:from-indigo-700 hover:to-purple-700 shadow-indigo-200'
                                                : 'bg-slate-900 text-white hover:bg-slate-800'
                                            }`}
                                    >
                                        {processingPlan === p.id ? (
                                            <>
                                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                                Processing...
                                            </>
                                        ) : (
                                            <>
                                                Purchase Add-on
                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                                                    <path strokeLinecap="round" strokeLinejoin="round" d="M13 7l5 5m0 0l-5 5m5-5H6" />
                                                </svg>
                                            </>
                                        )}
                                    </button>
                                </div>
                            </div>
                        );
                    })}
                </div>

                {/* Footer */}
                <div className="mt-16 flex flex-col items-center gap-4 text-slate-600">
                    <p className="text-base md:text-lg">
                        Need something custom? Our team can tailor a pack for enterprise workloads.
                    </p>
                    <button className="inline-flex items-center gap-2 text-indigo-600 hover:text-indigo-700 font-semibold text-base">
                        Contact sales
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M13 7l5 5m0 0l-5 5m5-5H6" />
                        </svg>
                    </button>
                </div>
            </div>

            {/* ====== MOCK CHECKOUT MODAL ====== */}
            {showMockCheckout && mockCheckoutPlan && createPortal(
                <div className="fixed inset-0 z-[99999] flex items-center justify-center p-4" style={{ background: 'rgba(15,23,42,0.7)', backdropFilter: 'blur(8px)' }}>
                    <div className="relative bg-white rounded-3xl shadow-2xl w-full max-w-md overflow-hidden">
                        {/* Header */}
                        <div className="bg-gradient-to-r from-slate-900 to-slate-800 px-6 pt-6 pb-5 text-white">
                            <div className="flex items-center justify-between mb-4">
                                <div className="flex items-center gap-2">
                                    <div className="w-8 h-8 rounded-full bg-indigo-500 flex items-center justify-center">
                                        <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                                        </svg>
                                    </div>
                                    <span className="text-sm font-bold text-slate-300">Secure Checkout</span>
                                </div>
                                <button
                                    onClick={() => { setShowMockCheckout(false); setMockCheckoutError(null); }}
                                    className="text-slate-400 hover:text-white transition text-xl leading-none"
                                    aria-label="Close"
                                >✕</button>
                            </div>
                            {/* Plan Summary */}
                            <div className="bg-white/10 rounded-xl px-4 py-3 flex items-center justify-between">
                                <div>
                                    <p className="text-xs text-slate-400 uppercase tracking-wider font-bold">Purchasing Add-on</p>
                                    <p className="text-lg font-black text-white mt-0.5">{mockCheckoutPlan.planName}</p>
                                </div>
                                <div className="text-right">
                                    <p className="text-2xl font-black text-white">
                                        ${parseFloat(mockCheckoutPlan.price || 0).toFixed(2)}
                                    </p>
                                    <p className="text-xs text-slate-400">one-time</p>
                                </div>
                            </div>
                        </div>

                        {/* Card Form or Success View */}
                        {mockCheckoutSuccess ? (
                            <div className="px-6 py-8 text-center space-y-6">
                                <div className="mx-auto w-20 h-20 bg-emerald-50 rounded-full flex items-center justify-center border border-emerald-100 shadow-inner">
                                    <span className="text-4xl animate-bounce" style={{ display: 'inline-block', animationDuration: '1.5s' }}>✅</span>
                                </div>

                                <div className="space-y-2">
                                    <h3 className="text-2xl font-black text-slate-900 tracking-tight">
                                        Payment Successful!
                                    </h3>
                                    <p className="text-sm text-slate-500 font-medium max-w-xs mx-auto">
                                        Your tokens have been successfully added to your account.
                                    </p>
                                </div>

                                <div className="bg-slate-50 rounded-2xl p-4 border border-slate-100/80 text-left space-y-2.5">
                                    <div className="flex justify-between text-xs font-semibold text-slate-400 uppercase tracking-wider">
                                        <span>Transaction Details</span>
                                        <span className="text-emerald-600 font-extrabold">Paid</span>
                                    </div>
                                    <div className="h-px bg-slate-200/50"></div>
                                    <div className="flex justify-between items-center">
                                        <span className="text-sm text-slate-500 font-medium">Pack</span>
                                        <span className="text-sm text-slate-800 font-bold">{mockCheckoutPlan.planName}</span>
                                    </div>
                                    <div className="flex justify-between items-center">
                                        <span className="text-sm text-slate-500 font-medium">Amount</span>
                                        <span className="text-sm text-slate-800 font-extrabold">
                                            ${parseFloat(mockCheckoutPlan.price || 0).toFixed(2)}
                                        </span>
                                    </div>
                                </div>

                                <button
                                    onClick={() => {
                                        setShowMockCheckout(false);
                                        setMockCheckoutSuccess(false);
                                        setMockCheckoutError(null);
                                        window.location.reload();
                                    }}
                                    className="w-full py-3.5 bg-emerald-600 hover:bg-emerald-700 text-white font-black rounded-xl text-sm transition duration-200 shadow-lg shadow-emerald-100"
                                >
                                    Done &amp; Refresh
                                </button>
                            </div>
                        ) : (
                            <form onSubmit={handleMockCheckoutSubmit} className="px-6 py-5 space-y-4">
                                {mockCheckoutError && (
                                    <div className="bg-rose-50 border border-rose-200 rounded-2xl px-4 py-3.5 text-sm text-rose-800 font-semibold flex items-start gap-3 shadow-sm">
                                        <span className="text-lg leading-none mt-0.5">❌</span>
                                        <div className="space-y-1">
                                            <p className="font-extrabold text-rose-900">Payment Failed</p>
                                            <p className="text-xs text-rose-700 font-normal leading-relaxed">{mockCheckoutError}</p>
                                        </div>
                                    </div>
                                )}

                                {/* Card Number */}
                                <div>
                                    <label className="block text-xs font-bold text-slate-600 uppercase tracking-wider mb-1.5">Card Number</label>
                                    <div className="relative">
                                        <input
                                            type="text"
                                            inputMode="numeric"
                                            placeholder="4242 4242 4242 4242"
                                            value={mockCardForm.number}
                                            onChange={e => setMockCardForm(f => ({ ...f, number: formatCardNumber(e.target.value) }))}
                                            className="w-full border border-slate-200 rounded-xl px-4 py-3 text-sm font-mono text-slate-800 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition pr-12"
                                            maxLength={19}
                                            required
                                        />
                                        <svg className="absolute right-3 top-3.5 w-5 h-5 text-slate-300" fill="currentColor" viewBox="0 0 24 24">
                                            <path d="M4 4h16a2 2 0 012 2v12a2 2 0 01-2 2H4a2 2 0 01-2-2V6a2 2 0 012-2zm0 5v7h16V9H4zm2 4h4v2H6v-2z"/>
                                        </svg>
                                    </div>
                                </div>

                                {/* Expiry + CVV */}
                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <label className="block text-xs font-bold text-slate-600 uppercase tracking-wider mb-1.5">Expiry (MM/YY)</label>
                                        <input
                                            type="text"
                                            inputMode="numeric"
                                            placeholder="12/28"
                                            value={mockCardForm.expiry}
                                            onChange={e => setMockCardForm(f => ({ ...f, expiry: formatExpiry(e.target.value) }))}
                                            className="w-full border border-slate-200 rounded-xl px-4 py-3 text-sm font-mono text-slate-800 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition"
                                            maxLength={5}
                                            required
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-bold text-slate-600 uppercase tracking-wider mb-1.5">CVV</label>
                                        <input
                                            type="text"
                                            inputMode="numeric"
                                            placeholder="123"
                                            value={mockCardForm.cvv}
                                            onChange={e => setMockCardForm(f => ({ ...f, cvv: e.target.value.replace(/\D/g, '').slice(0, 4) }))}
                                            className="w-full border border-slate-200 rounded-xl px-4 py-3 text-sm font-mono text-slate-800 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition"
                                            maxLength={4}
                                            required
                                        />
                                    </div>
                                </div>

                                {/* Cardholder Name */}
                                <div>
                                    <label className="block text-xs font-bold text-slate-600 uppercase tracking-wider mb-1.5">Cardholder Name</label>
                                    <input
                                        type="text"
                                        placeholder="Sathsih"
                                        value={mockCardForm.name}
                                        onChange={e => setMockCardForm(f => ({ ...f, name: e.target.value }))}
                                        className="w-full border border-slate-200 rounded-xl px-4 py-3 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition"
                                        required
                                    />
                                </div>

                                {/* Trust Badges */}
                                <div className="flex items-center gap-4 py-1">
                                    <div className="flex items-center gap-1.5 text-xs text-slate-400 font-medium">
                                        <span className="text-green-500">✓</span>
                                        <span>256-bit SSL</span>
                                    </div>
                                    <div className="flex items-center gap-1.5 text-xs text-slate-400 font-medium">
                                        <span className="text-indigo-500">✓</span>
                                        <span>Secure via Paddle</span>
                                    </div>
                                </div>

                                {/* Submit */}
                                <button
                                    type="submit"
                                    disabled={mockCheckoutProcessing}
                                    className="w-full py-3.5 bg-indigo-600 hover:bg-indigo-700 disabled:bg-indigo-400 text-white font-black rounded-xl text-sm transition duration-200 shadow-lg flex items-center justify-center gap-2"
                                >
                                    {mockCheckoutProcessing ? (
                                        <>
                                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                            Processing...
                                        </>
                                    ) : (
                                        <>🔒 Pay ${parseFloat(mockCheckoutPlan.price || 0).toFixed(2)} &amp; Purchase Pack</>
                                    )}
                                </button>
                            </form>
                        )}
                    </div>
                </div>,
                document.body
            )}
        </div>
    );
};
// End Code - dc - 11062025

export default AddOnPlans;


